import { useSelector } from 'react-redux';
import { VehicleType } from '../enums/VehicleType';
import { AppState } from '../store/AppState';
import { MinMaxFilter } from '../store/inventory/InventoryFilterActions';
import { InventoryFilterState } from '../store/inventory/InventoryFilterReducer';
import { InventoryState } from '../store/inventory/InventoryReducer';
import { InventoryResource } from '../types/InvetoryResource';

export const useFilteredInventoryData = (): UseFilteredInventoryDataReturnType => {
  const { inventoryData, lastUpdated, loading, error, remainingModal, remainingBulkInventory } =
    useSelector<AppState, InventoryState>((state) => state.inventoryReducer);
  const filters = useSelector<AppState, InventoryFilterState>((state) => state.inventoryFilter);
  const filteredData: InventoryResource[] = applyFilters(inventoryData, filters);
  const count: number = filteredData.length;
  return {
    inventoryData: filteredData,
    unfilteredData: inventoryData,
    count: count,
    error,
    loading,
    lastUpdated,
    remainingModal,
    remainingBulkInventory,
  };
};

export const applyFilters = (
  inventoryData: InventoryResource[],
  filters: InventoryFilterState,
): InventoryResource[] => {
  let filteredData = inventoryData
    .filter((data) => filterDataWithSeachedTextOnly(data, filters.search))
    .filter((data) => filterDataWithGivenVehicleTypesOnly(data, filters.vehicleTypes))
    .filter((data) => filterDataWithGivenYearsOnly(data, filters.years))
    .filter((data) => filterDataWithGivenBrandsOnly(data, filters.brands))
    .filter((data) => filterDataWithGivenModelsOnly(data, filters.models))
    .filter((data) => filterDataWithGivenTrimsOnly(data, filters.trims))
    .filter((data) => filterDataWithGivenExteriorColorOnly(data, filters.exteriorColor))
    .filter((data) => filterDataWithGivenInteriorColorOnly(data, filters.interiorColor))
    .filter((data) => filterDataWithinGivenMileageRange(data, filters.mileage))
    .filter((data) => filterDataWithinGivenDaysOnRange(data, filters.ageDays))
    .filter((data) => filterDataWithinGivenPriceRange(data, filters.price))
    .sort((a, b) => b.year - a.year);

  return filteredData;
};

const filterDataWithSeachedTextOnly = (data: InventoryResource, search: string) => {
  const formattedSearchText = search.trim().toLowerCase();
  const formattedItem = {
    model: data?.model?.toLowerCase(),
    stockNumber: data?.stockNumber?.toLowerCase(),
    year: data?.year.toString(),
    brand: data?.brandName.toLowerCase(),
    trim: data?.trim?.toLowerCase(),
    vin: data?.vin?.toLowerCase(),
  };
  return (
    formattedItem?.model?.includes(formattedSearchText) ||
    formattedItem?.stockNumber?.includes(formattedSearchText) ||
    formattedItem?.year?.includes(formattedSearchText) ||
    formattedItem?.brand?.includes(formattedSearchText) ||
    formattedItem?.trim?.includes(formattedSearchText) ||
    formattedItem?.vin?.includes(formattedSearchText) ||
    (
      formattedItem.model +
      ' ' +
      formattedItem.stockNumber +
      ' ' +
      formattedItem.year +
      ' ' +
      formattedItem.brand +
      ' ' +
      formattedItem.trim +
      ' ' +
      formattedItem.vin
    )?.includes(formattedSearchText)
  );
};

export const filterDataWithGivenVehicleTypesOnly = (
  data: InventoryResource,
  types: VehicleType[],
): boolean => {
  if (types.includes(VehicleType.New) && data.vehicleType === 'New') {
    return true;
  }

  if (types.includes(VehicleType.PreOwned) && data.vehicleType === 'Pre-owned') {
    return true;
  }

  if (types.includes(VehicleType.CertifiedPreOwned) && data.vehicleType === 'Certified Pre-owned') {
    return true;
  }

  if (types.includes(VehicleType.Sample) && data.vehicleType === null) {
    return true;
  }

  if (types.length === 0) {
    return true;
  }

  return false;
};

const filterDataWithGivenYearsOnly = (data: InventoryResource, years: readonly number[]) => {
  if (years.includes(data.year)) return true;
  if (years.length === 0) return true;
};

export const filterDataWithGivenBrandsOnly = (
  data: InventoryResource,
  brands: string[],
): boolean => {
  return brands.length === 0 || brands.includes(data.brandName);
};

export const filterDataWithGivenModelsOnly = (
  data: InventoryResource,
  models: string[],
): boolean => {
  return models.length === 0 || models.includes(data.model);
};

export const filterDataWithGivenTrimsOnly = (data: InventoryResource, trims: string[]): boolean => {
  return trims.length === 0 || trims.includes(data.trim);
};

export const filterDataWithGivenExteriorColorOnly = (
  data: InventoryResource,
  exteriorColor: string[],
): boolean => {
  return exteriorColor.length === 0 || exteriorColor.includes(data.colorExt);
};

export const filterDataWithGivenInteriorColorOnly = (
  data: InventoryResource,
  interiorColor: string[],
): boolean => {
  return interiorColor.length === 0 || interiorColor.includes(data.colorInt);
};

export const filterDataWithinGivenMileageRange = (
  data: InventoryResource,
  mileage: MinMaxFilter,
) => {
  let max = mileage.max === -1 ? 10000000 : mileage.max;
  if ((data.miles || 0) >= mileage.min && (data.miles || 0) <= max) return true;
};

export const filterDataWithinGivenDaysOnRange = (
  data: InventoryResource,
  ageDays: MinMaxFilter,
) => {
  let max = ageDays.max === -1 ? 10000000 : ageDays.max;
  if ((data.ageDays || 0) >= ageDays.min && (data.ageDays || 0) <= max) return true;
};

export const filterDataWithinGivenPriceRange = (data: InventoryResource, price: MinMaxFilter) => {
  let max = price.max === -1 ? 10000000 : price.max;
  if (Math.round(data.listPrice) >= price.min && Math.round(data.listPrice) <= max) return true;
};

export type UseFilteredInventoryDataReturnType = InventoryState & {
  count: number;
  unfilteredData: readonly InventoryResource[];
};
